import { css } from "@emotion/react";
import { Button, Theme, useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useState } from "react";
import { match } from "ts-pattern";

import Callout from "~/components/common/Callout";
import { useTableContent } from "~/components/database/detail/database-content/lib";
import ResponseContent from "~/components/item/[id]/response-content";
import { DatabaseDetailTableContentQuery } from "~/lib/graphql/client";
import { DatabaseDetailPageProps } from "~/pages/database/[id]";

export default function DatabaseResponseItemView({
  props,
  responseId,
}: {
  props: DatabaseDetailPageProps;
  responseId?: number;
}) {
  const tableContent = useTableContent(props.form.id);

  return match(tableContent)
    .with({ status: "pending" }, () => <div>Loading...</div>)
    .with({ status: "success" }, ({ data }) => {
      if (data.form.responses.length === 0) {
        return (
          <div
            css={css`
              padding: 0.5rem;
            `}
          >
            <Callout kind={"info"}>
              <p
                css={css`
                  font-size: 0.8rem;
                `}
              >
                まだ回答がありません。
                <br />
                データベースに書き込んで情報を充実させよう！
              </p>
            </Callout>
          </div>
        );
      }

      return (
        <DatabaseResponseItemViewBody
          props={props}
          responseId={responseId}
          tableContent={data}
        />
      );
    })
    .with({ status: "error" }, () => <div>Error</div>)
    .exhaustive();
}

const DatabaseResponseItemViewBody = ({
  props,
  responseId,
  tableContent,
}: {
  props: DatabaseDetailPageProps;
  responseId?: number;
  tableContent: DatabaseDetailTableContentQuery;
}) => {
  const { response, nextResponse, prevResponse, enableNext, enablePrev } =
    useResponse(responseId, tableContent.form.responses);
  const theme = useTheme();
  return (
    <div css={rootStyle(theme)}>
      <div css={buttonGroupStyle}>
        <Button
          onClick={prevResponse}
          disabled={!enablePrev}
          id={"prev-response"}
        >
          Prev
        </Button>
        <Button
          onClick={nextResponse}
          disabled={!enableNext}
          id={"next-response"}
        >
          Next
        </Button>
      </div>
      <div>
        <ResponseContent questions={props.form.questions} response={response} />
      </div>
    </div>
  );
};

const rootStyle = (theme: Theme) => css`
  min-height: 80dvh;
  background-color: ${theme.palette.background.paper};
  padding: ${theme.spacing(1)};
`;

const buttonGroupStyle = css`
  display: flex;
  justify-content: space-between;
`;

function useResponse(
  responseId: number | null | undefined,
  responses: DatabaseDetailTableContentQuery["form"]["responses"]
): {
  response: DatabaseDetailTableContentQuery["form"]["responses"][number];
  nextResponse: () => void;
  prevResponse: () => void;
  enableNext: boolean;
  enablePrev: boolean;
} {
  const router = useRouter();
  const firstResponseIndex: number = (() => {
    let firstResponseIndex =
      responseId !== undefined &&
      responses.findIndex((response) => response.id === responseId);
    if (firstResponseIndex === -1 || !firstResponseIndex) {
      firstResponseIndex = 0;
    }

    return firstResponseIndex;
  })();
  const [response, setResponse] = useState(responses[firstResponseIndex]);

  const nextResponse = () => {
    setResponse((prev) => {
      const nextIndex = responses.findIndex((r) => r.id === prev.id) + 1;
      if (nextIndex < responses.length) {
        return responses[nextIndex];
      } else {
        return prev;
      }
    });
  };

  const prevResponse = () => {
    setResponse((prev) => {
      const prevIndex = responses.findIndex((r) => r.id === prev.id) - 1;
      if (prevIndex >= 0) {
        return responses[prevIndex];
      } else {
        return prev;
      }
    });
  };

  const enableNext =
    responses.findIndex((r) => r.id === response.id) < responses.length - 1;
  const enablePrev = responses.findIndex((r) => r.id === response.id) > 0;

  return {
    response,
    nextResponse,
    prevResponse,
    enableNext,
    enablePrev,
  };
}
